import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';

/**
 * Paths that should never be blocked by the onboarding guard.
 * These are checked as prefixes against the current pathname.
 */
const ONBOARDING_EXEMPT_PATHS = [
  '/onboarding',
  '/callback',
  '/login',
  '/invitations/',
];

/**
 * Route guard that redirects users to the onboarding wizard
 * if they haven't completed onboarding yet.
 *
 * - Skips the check if user is null (not authenticated yet).
 * - Allows exempt paths (onboarding, callback, login, invitations) through.
 * - Redirects all other paths to /onboarding when onboarding_completed_at is falsy.
 */
export function OnboardingGuard() {
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();

  // If user is not loaded yet, let the auth guard handle it
  if (!user) {
    return <Outlet />;
  }

  // If onboarding is completed, render normally
  if (user.onboarding_completed_at) {
    return <Outlet />;
  }

  // Check if the current path is exempt from the onboarding requirement
  const isExempt = ONBOARDING_EXEMPT_PATHS.some((path) =>
    pathname.startsWith(path)
  );

  if (isExempt) {
    return <Outlet />;
  }

  // User hasn't completed onboarding and is trying to access a protected route
  return <Navigate to="/onboarding" replace />;
}
